import { config, isDev } from "@/config/configuration";
import axios from "axios";
import { useEffect, useState } from "react";

interface VersionFile {
  version?: string;
  commitHash?: string;
}

const VERSION_FILE_PATH = "/version.json";
const VERSION_POLL_INTERVAL_MS = 60_000;
const versionFileAxios = axios.create();

function reloadPage() {
  window.location.reload();
}

export function useAppVersionRefresh(onVersionMismatch: () => void = reloadPage) {
  const [isOnline, setIsOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));

  useEffect(() => {
    const handleOffline = () => setIsOnline(false);
    const handleOnline = () => setIsOnline(true);

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  useEffect(() => {
    if (!isOnline || isDev) {
      return;
    }

    let isCancelled = false;

    const checkVersion = async () => {
      try {
        const response = await versionFileAxios.get<VersionFile>(VERSION_FILE_PATH, {
          params: { t: Date.now() },
        });

        if (isCancelled) {
          return;
        }

        const remoteVersion = response.data?.version;
        const remoteCommitHash = response.data?.commitHash;

        if (typeof remoteVersion !== "string" || typeof remoteCommitHash !== "string") {
          return;
        }

        if (remoteVersion !== config.version || remoteCommitHash !== config.commitHash) {
          onVersionMismatch();
        }
      } catch {
        // Intentionally silent to avoid user-facing errors.
      }
    };

    void checkVersion();

    const intervalId = window.setInterval(() => {
      void checkVersion();
    }, VERSION_POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isOnline, onVersionMismatch]);
}
